import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { getProductCategories } from 'api/products'

interface CategoryRow {
  category: string
  rate: string
}

interface DiscountRateBuilderProps {
  initialRates?: Record<string, number>
  onChange: (rates: Record<string, number>) => void
}

const HANDLES_LABEL = 'Handles'
const isHandlesCategory = (cat: string) =>
  cat.startsWith('Handle') || cat.startsWith('Knob')

const toPercentStr = (val: number) => String(Math.round(val * 100))

const blockDecimal = (e: React.KeyboardEvent) => {
  if (e.key === '.' || e.key === ',') e.preventDefault()
}

const DiscountRateBuilder = ({
  initialRates,
  onChange
}: DiscountRateBuilderProps) => {
  const [allCategories, setAllCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  const [rows, setRows] = useState<CategoryRow[]>(() => {
    if (!initialRates) return []
    return Object.entries(initialRates)
      .filter(([k]) => k !== 'default')
      .map(([k, v]) => ({ category: k, rate: toPercentStr(v) }))
  })

  const [defaultRate, setDefaultRate] = useState(() =>
    initialRates?.default !== undefined
      ? toPercentStr(initialRates.default)
      : ''
  )

  useEffect(() => {
    getProductCategories().then(cats => {
      const merged = [
        ...cats.filter(c => !isHandlesCategory(c)),
        ...(cats.some(isHandlesCategory) ? [HANDLES_LABEL] : [])
      ]
      setAllCategories(merged)
      setLoading(false)
    })
  }, [])

  const buildResult = (r: CategoryRow[], def: string) => {
    const result: Record<string, number> = {}
    for (const { category, rate } of r) {
      if (!category) continue
      const percent = parseInt(rate, 10)
      if (!isNaN(percent)) result[category] = percent / 100
    }
    const parsedDefault = parseInt(def, 10)
    if (!isNaN(parsedDefault)) result['default'] = parsedDefault / 100
    return result
  }

  useEffect(() => {
    onChange(buildResult(rows, defaultRate))
  }, [rows, defaultRate])

  const selectedCategories = new Set(rows.map(r => r.category).filter(Boolean))

  const availableFor = (rowCategory: string) =>
    allCategories.filter(c => c === rowCategory || !selectedCategories.has(c))

  const addRow = () => setRows(prev => [...prev, { category: '', rate: '' }])
  const removeRow = (i: number) =>
    setRows(prev => prev.filter((_, idx) => idx !== i))
  const updateRow = (i: number, patch: Partial<CategoryRow>) =>
    setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  const canAdd = !loading && selectedCategories.size < allCategories.length

  const content = (
    <Box>
      {/* Column headers */}
      {rows.length > 0 && (
        <Stack direction='row' spacing={1} mb={0.5} px={0.5}>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ width: 150 }}
          >
            Category
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ width: 120 }}
          >
            Discount Rate
          </Typography>
        </Stack>
      )}

      {/* Category rows */}
      {rows.map((row, i) => (
        <Stack key={i} direction='row' spacing={1} alignItems='center' mb={1}>
          <Select
            size='small'
            displayEmpty
            value={row.category}
            onChange={e => updateRow(i, { category: e.target.value })}
            sx={{
              width: 150,
              bgcolor: 'background.paper',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' }
            }}
            disabled={loading}
          >
            <MenuItem value='' disabled>
              <Typography variant='body2' color='text.disabled'>
                {loading ? 'Loading…' : 'Select…'}
              </Typography>
            </MenuItem>
            {availableFor(row.category).map(cat => (
              <MenuItem key={cat} value={cat}>
                <Chip
                  label={cat}
                  size='small'
                  sx={{ fontWeight: 600, fontSize: 12 }}
                />
              </MenuItem>
            ))}
          </Select>

          <TextField
            size='small'
            type='number'
            placeholder='0'
            value={row.rate}
            onChange={e => updateRow(i, { rate: e.target.value })}
            onKeyDown={blockDecimal}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <Typography variant='body2' color='text.secondary'>
                    %
                  </Typography>
                </InputAdornment>
              ),
              inputProps: { min: 0, max: 100, step: 1 }
            }}
            sx={{ width: 120 }}
          />

          <Tooltip title='Remove'>
            <IconButton
              size='small'
              onClick={() => removeRow(i)}
              sx={{
                color: 'text.disabled',
                '&:hover': { color: 'error.main' }
              }}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}

      <Button
        size='small'
        variant='text'
        startIcon={loading ? <CircularProgress size={13} /> : <AddIcon />}
        onClick={addRow}
        disabled={!canAdd}
        sx={{ color: 'primary.main', mb: 1, pl: 0.5 }}
      >
        Add category
      </Button>

      <Divider sx={{ my: 1.5 }} />

      {/* Default row */}
      <Stack direction='row' spacing={1} alignItems='center' mb={1.5}>
        <Box
          sx={{
            width: 150,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Chip
            label='Default'
            size='small'
            variant='outlined'
            sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary' }}
          />
          {rows.length > 0 && (
            <Typography variant='caption' color='text.disabled'>
              (all others)
            </Typography>
          )}
        </Box>

        <TextField
          size='small'
          type='number'
          placeholder='0'
          value={defaultRate}
          onChange={e => setDefaultRate(e.target.value)}
          onKeyDown={blockDecimal}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Typography variant='body2' color='text.secondary'>
                  %
                </Typography>
              </InputAdornment>
            ),
            inputProps: { min: 0, max: 100, step: 1 }
          }}
          sx={{ width: 120 }}
        />
      </Stack>
    </Box>
  )

  return content
}

export default DiscountRateBuilder
